import { useState, useEffect, useRef, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

type GalleryItem = {
  id: number;
  src: string;
  category: string;
  title: string;
  description: string;
};

type SearchInputProps = {
  initialQuery?: string;
  placeholder?: string;
  onSearch?: (query: string) => void;
  className?: string;
  autoFocus?: boolean;
  showSuggestions?: boolean;
  galleryData?: GalleryItem[];
};

const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>((props, ref) => {
  const {
    initialQuery = '',
    placeholder = 'Search our work...',
    onSearch,
    className = '',
    autoFocus = false,
    showSuggestions = true,
    galleryData = [],
  } = props;

  const [query, setQuery] = useState(initialQuery);
  const [isFocused, setIsFocused] = useState(false);
  const [suggestions, setSuggestions] = useState<GalleryItem[]>([]);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update suggestions when query changes
  useEffect(() => {
    if (query.trim() === '') {
      setSuggestions([]);
      return;
    }

    const searchTerm = query.toLowerCase();
    const results = (galleryData || []).filter(
      (item) =>
        item.title.toLowerCase().includes(searchTerm) ||
        item.description.toLowerCase().includes(searchTerm) ||
        item.category.toLowerCase().includes(searchTerm)
    );

    // Show top 5 suggestions
    setSuggestions(results.slice(0, 5));
  }, [query, galleryData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const searchQuery = query.trim();
    if (searchQuery) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setIsFocused(false);
      onSearch?.(searchQuery);
    }
  };

  const handleSuggestionClick = (item: GalleryItem) => {
    setQuery(item.title);
    navigate(`/search?q=${encodeURIComponent(item.title)}`);
    setIsFocused(false);
    onSearch?.(item.title);
  };

  // Handle ref forwarding
  const handleRef = (node: HTMLInputElement | null) => {
    inputRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
    }
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <form onSubmit={handleSubmit} className="relative">
        <input
          ref={handleRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          placeholder={placeholder}
          className="w-full px-6 py-3 md:py-4 bg-gray-800 border border-gray-700 rounded-full text-white focus:outline-none focus:ring-2 focus:ring-burnt-orange focus:border-transparent pr-12 text-base md:text-lg"
          aria-label="Search"
          autoFocus={autoFocus}
        />
        <button
          type="submit"
          className="absolute right-2 top-1/2 -translate-y-1/2 bg-burnt-orange hover:bg-burnt-orange/90 text-white p-2 rounded-full transition-colors"
          title="Search"
          aria-label="Search"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </button>
      </form>

      {/* Suggestions Dropdown */}
      <AnimatePresence>
        {showSuggestions && isFocused && suggestions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            className="absolute z-50 w-full mt-2 bg-gray-800 border border-gray-700 rounded-xl shadow-xl overflow-hidden"
          >
            <div className="py-1">
              {suggestions.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleSuggestionClick(item)}
                  className="w-full text-left px-4 py-3 hover:bg-gray-700/50 transition-colors flex items-center group"
                >
                  <div className="w-10 h-10 rounded-md overflow-hidden flex-shrink-0 mr-3">
                    <img
                      src={item.src}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'https://placehold.co/100x100/1f2937/6b7280?text=Image';
                      }}
                    />
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-white truncate">{item.title}</div>
                    <div className="text-xs text-gray-400 capitalize">{item.category}</div>
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

SearchInput.displayName = 'SearchInput';

export default SearchInput;
